import type { OpeningGroupListItem } from "~/lib/openingGroups";
import { parseOpeningGroupIdString } from "~/lib/openingGroups";
import { HOME_PAGE_CONTROL_STORAGE_KEY } from "~/components/ui/page-control-storage";

export type PendingHomeOpeningGroupSelection =
  | { kind: "tasks" }
  | { kind: "opening-group"; problemSetId: number }
  | { kind: "first-opening-group" };

export type HomeOpeningGroupSelection =
  | { kind: "tasks" }
  | { kind: "opening-group"; problemSetId: number };

function getStorage(storage?: Storage): Storage | null {
  if (storage) return storage;
  return typeof window === "undefined" ? null : window.localStorage;
}

export function readPendingHomeOpeningGroupSelection(
  storage?: Storage,
): PendingHomeOpeningGroupSelection {
  try {
    const stored = getStorage(storage)?.getItem(HOME_PAGE_CONTROL_STORAGE_KEY);
    if (stored === "openings") return { kind: "first-opening-group" };
    if (stored === "tasks") return { kind: "tasks" };

    const prefix = "opening-group:";
    if (stored?.startsWith(prefix)) {
      const problemSetId = parseOpeningGroupIdString(
        stored.slice(prefix.length),
      );
      if (problemSetId !== null) {
        return { kind: "opening-group", problemSetId };
      }
    }
  } catch {
    return { kind: "tasks" };
  }

  return { kind: "tasks" };
}

export function resolveHomeOpeningGroupSelection(
  pending: PendingHomeOpeningGroupSelection,
  groups: readonly OpeningGroupListItem[],
): HomeOpeningGroupSelection {
  if (pending.kind === "tasks") return pending;
  if (
    pending.kind === "opening-group" &&
    groups.some((group) => group.problemSetId === pending.problemSetId)
  ) {
    return pending;
  }

  const firstGroup = groups[0];
  return firstGroup
    ? { kind: "opening-group", problemSetId: firstGroup.problemSetId }
    : { kind: "tasks" };
}

export function persistHomeOpeningGroupSelection(
  selection: HomeOpeningGroupSelection,
  storage?: Storage,
): void {
  try {
    getStorage(storage)?.setItem(
      HOME_PAGE_CONTROL_STORAGE_KEY,
      selection.kind === "tasks"
        ? "tasks"
        : `opening-group:${selection.problemSetId}`,
    );
  } catch {
    // Selection remains usable in memory when local storage is unavailable.
  }
}
